/**
 * Typed admin REST client for admin-service (`/v1/admin/*`).
 * All methods unwrap the standard `{ success, data, error }` envelope via {@link unwrap}.
 */

import { api, unwrap, ApiError } from './client';

export { ApiError };

/**
 * @param {string} path
 * @param {Record<string, unknown>} [params]
 * @returns {Promise<unknown>}
 */
async function get(path, params) {
  const res = await api.get(path, { params });
  return unwrap(res.data);
}

/**
 * @param {string} path
 * @param {Record<string, unknown>} [body]
 * @returns {Promise<unknown>}
 */
async function post(path, body) {
  const res = await api.post(path, body);
  return unwrap(res.data);
}

/**
 * @param {string} path
 * @param {Record<string, unknown>} [body]
 * @returns {Promise<unknown>}
 */
async function patch(path, body) {
  const res = await api.patch(path, body);
  return unwrap(res.data);
}

/**
 * @param {string} path
 * @returns {Promise<unknown>}
 */
async function del(path) {
  const res = await api.delete(path);
  return unwrap(res.data);
}

/** @param {{ email: string, password: string }} payload */
export async function loginAdmin(payload) {
  const res = await api.post('/v1/admin/auth/login', payload);
  return unwrap(res.data);
}

/** Clear httpOnly refresh cookie and end admin session. */
export async function logoutAdmin() {
  await api.post('/v1/admin/auth/logout');
}

/** @returns {Promise<unknown>} Current admin profile. */
export async function fetchMe() {
  return get('/v1/admin/auth/me');
}

/** @param {{ email: string }} payload */
export async function forgotPassword(payload) {
  return post('/v1/admin/auth/forgot-password', payload);
}

/** @param {{ token: string, password: string }} payload */
export async function resetPassword(payload) {
  return post('/v1/admin/auth/reset-password', payload);
}

/** Rotate access token using httpOnly refresh cookie. */
export async function refreshAdminToken() {
  return post('/v1/admin/auth/refresh');
}

/** @returns {Promise<unknown>} Dashboard KPI counts. */
export async function fetchDashboardCounts() {
  return get('/v1/admin/dashboard');
}

/** @param {Record<string, unknown>} [params] */
export async function listCategories(params) {
  return get('/v1/admin/categories', params);
}

/** @param {string} categoryId */
export async function getCategory(categoryId) {
  return get(`/v1/admin/categories/${categoryId}`);
}

/** @param {Record<string, unknown>} body */
export async function createCategory(body) {
  return post('/v1/admin/categories', body);
}

/** @param {string} categoryId @param {Record<string, unknown>} body */
export async function updateCategory(categoryId, body) {
  return patch(`/v1/admin/categories/${categoryId}`, body);
}

/** @param {string} categoryId */
export async function deleteCategory(categoryId) {
  return del(`/v1/admin/categories/${categoryId}`);
}

/** @param {Record<string, unknown>} [params] */
export async function listMenuItems(params) {
  return get('/v1/admin/menu-items', params);
}

/** @param {string} menuItemId */
export async function getMenuItem(menuItemId) {
  return get(`/v1/admin/menu-items/${menuItemId}`);
}

/** @param {Record<string, unknown>} body */
export async function createMenuItem(body) {
  return post('/v1/admin/menu-items', body);
}

/** @param {string} menuItemId @param {Record<string, unknown>} body */
export async function updateMenuItem(menuItemId, body) {
  return patch(`/v1/admin/menu-items/${menuItemId}`, body);
}

/** @param {string} menuItemId */
export async function deleteMenuItem(menuItemId) {
  return del(`/v1/admin/menu-items/${menuItemId}`);
}

/** Active fulfillment queue — full list, no pagination (excludes DELIVERED, REFUNDED, CANCELLED). */
export async function listActiveOrders(params) {
  return get('/v1/admin/orders/lists/active', params);
}

/** Closed orders only (DELIVERED, REFUNDED, CANCELLED). */
export async function listOrderHistory(params) {
  return get('/v1/admin/orders/lists/history', params);
}

/** Unscoped list — use for per-user order lookup on app-user detail. */
export async function listOrders(params) {
  return get('/v1/admin/orders', params);
}

/** @param {string} orderId Business UUID */
export async function getOrder(orderId) {
  return get(`/v1/admin/orders/${orderId}`);
}

/** @param {string} orderId @param {{ status: string }} body */
export async function patchOrderStatus(orderId, body) {
  return patch(`/v1/admin/orders/${orderId}/status`, body);
}

/**
 * Cancel an active order. ONLINE/SUCCESS payments are auto-refunded via Razorpay;
 * COD orders only update status. Throws on PG failure (admin can retry).
 * @param {string} orderId Business UUID
 * @param {{ reason: string }} body
 */
export async function cancelOrder(orderId, body) {
  return post(`/v1/admin/orders/${orderId}/cancel`, body);
}

/**
 * Refund a delivered order. ONLINE orders go through Razorpay; COD requires `manual: true`.
 * @param {string} orderId Business UUID
 * @param {{ reason: string, manual?: boolean }} body
 */
export async function refundOrder(orderId, body) {
  return post(`/v1/admin/orders/${orderId}/refund`, body);
}

/** @param {Record<string, unknown>} [params] */
export async function listPayments(params) {
  return get('/v1/admin/payments', params);
}

/** @param {string} paymentId */
export async function getPayment(paymentId) {
  return get(`/v1/admin/payments/${paymentId}`);
}

/** @param {Record<string, unknown>} [params] */
export async function listReviews(params) {
  return get('/v1/admin/reviews', params);
}

/** @param {string} reviewId */
export async function getReview(reviewId) {
  return get(`/v1/admin/reviews/${reviewId}`);
}

/** @param {string} reviewId */
export async function deleteReview(reviewId) {
  return del(`/v1/admin/reviews/${reviewId}`);
}

/** @param {Record<string, unknown>} [params] */
export async function listFeedback(params) {
  return get('/v1/admin/feedback', params);
}

/** @param {string} feedbackId */
export async function getFeedback(feedbackId) {
  return get(`/v1/admin/feedback/${feedbackId}`);
}

/** @param {Record<string, unknown>} [params] */
export async function listSupportTickets(params) {
  return get('/v1/admin/support-tickets', params);
}

/** @param {string} ticketId */
export async function getSupportTicket(ticketId) {
  return get(`/v1/admin/support-tickets/${ticketId}`);
}

/** @param {string} ticketId @param {{ status: string }} body */
export async function patchSupportTicketStatus(ticketId, body) {
  return patch(`/v1/admin/support-tickets/${ticketId}/status`, body);
}

/** @param {Record<string, unknown>} [params] */
export async function listRewards(params) {
  return get('/v1/admin/rewards', params);
}

/** @param {string} rewardId */
export async function getReward(rewardId) {
  return get(`/v1/admin/rewards/${rewardId}`);
}

/** @param {Record<string, unknown>} body */
export async function createReward(body) {
  return post('/v1/admin/rewards', body);
}

/** @param {string} rewardId @param {Record<string, unknown>} body */
export async function updateReward(rewardId, body) {
  return patch(`/v1/admin/rewards/${rewardId}`, body);
}

/** @param {string} rewardId */
export async function deleteReward(rewardId) {
  return del(`/v1/admin/rewards/${rewardId}`);
}

/** @param {Record<string, unknown>} [params] */
export async function listAppUsers(params) {
  return get('/v1/admin/app-users', params);
}

/** @param {{ folder: string, fileName: string, contentType: string }} body */
export async function createSignedUploadUrl(body) {
  return post('/v1/admin/uploads/signed-url', body);
}

/** @param {string} userId */
export async function getAppUser(userId) {
  return get(`/v1/admin/app-users/${userId}`);
}

/** @param {string} userId @param {Record<string, unknown>} body */
export async function updateAppUser(userId, body) {
  return patch(`/v1/admin/app-users/${userId}`, body);
}

/** @returns {Promise<unknown>} App metadata (fees, store flags, etc.). */
export async function fetchMetadata() {
  return get('/v1/admin/metadata');
}

/** Period analytics — preset: today | yesterday | week | month | custom (+ dateFrom/dateTo). */
export async function fetchAnalytics(params) {
  return get('/v1/admin/analytics', params);
}

/** Per-order commission flow by orderNumber (e.g. ORD-ABC12345). */
export async function fetchOrderCommissionFlow(orderKey) {
  return get(`/v1/admin/orders/commission-flow/${encodeURIComponent(orderKey)}`);
}

/** @param {Record<string, unknown>} body */
export async function patchMetadata(body) {
  return patch('/v1/admin/metadata', body);
}

/** @returns {Promise<unknown[]>} Privacy policy + terms (markdown). */
export async function listLegalPages() {
  return get('/v1/admin/legal-pages');
}

/** @param {string} slug - `privacy-policy` | `terms-and-conditions` */
export async function fetchLegalPage(slug) {
  return get(`/v1/admin/legal-pages/${encodeURIComponent(slug)}`);
}

/** @param {string} slug @param {{ title?: string, markdown?: string }} body */
export async function updateLegalPage(slug, body) {
  return patch(`/v1/admin/legal-pages/${encodeURIComponent(slug)}`, body);
}

/** Cafe open/closed toggle (broadcasts to customer app via Socket.IO). */
export async function fetchStoreStatus() {
  return get('/v1/admin/store/status');
}

/** @param {{ isOpen: boolean }} body */
export async function patchStoreStatus(body) {
  return patch('/v1/admin/store/status', body);
}

/** @param {Record<string, unknown>} [params] */
export async function listBanners(params) {
  return get('/v1/admin/banners', params);
}

/** @param {string} bannerId */
export async function getBanner(bannerId) {
  return get(`/v1/admin/banners/${bannerId}`);
}

/** @param {Record<string, unknown>} body */
export async function createBanner(body) {
  return post('/v1/admin/banners', body);
}

/** @param {string} bannerId @param {Record<string, unknown>} body */
export async function updateBanner(bannerId, body) {
  return patch(`/v1/admin/banners/${bannerId}`, body);
}

/** @param {string} bannerId */
export async function deleteBanner(bannerId) {
  return del(`/v1/admin/banners/${bannerId}`);
}

